import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  LinearProgress,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from '@mui/material';
import {
  Add,
  Delete,
  Description,
  CheckCircle,
  AttachMoney,
  MoreVert,
  Edit,
  Business,
  Visibility,
  FileDownload,
  AccountBalance,
  Print,
  Assessment,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { baseClient } from '../api/baseClient';
import BusinessPlanSimulator from '../components/BusinessPlanSimulator';
import AIDisclaimer from '../components/AIDisclaimer';
import FundingApplicationConfirmDialog from '../components/FundingApplicationConfirmDialog';

// Use the centralized baseClient which has Firebase auth support
const api = baseClient;

// Enhanced Business Plan Interface
interface BusinessPlan {
  id: string;
  name: string;
  account_id?: string;
  status: 'draft' | 'in_progress' | 'completed' | 'review';
  completion_percentage: number;
  priority: 'low' | 'medium' | 'high';
  data: {
    executiveSummary?: {
      overview?: string;
      mission?: string;
      vision?: string;
      keyFactors?: string[];
    };
    companyDescription?: {
      history?: string;
      ownership?: string;
      location?: string;
      facilities?: string;
      products?: string[];
      services?: string[];
      competitiveAdvantages?: string[];
    };
    marketAnalysis?: {
      industryOverview?: string;
      targetMarket?: string;
      marketSize?: string;
      marketTrends?: string;
      competitorAnalysis?: string;
      swotAnalysis?: {
        strengths?: string[];
        weaknesses?: string[];
        opportunities?: string[];
        threats?: string[];
      };
    };
    organizationManagement?: {
      organizationalStructure?: string;
      managementTeam?: Array<{
        name: string;
        role: string;
        experience: string;
        education: string;
      }>;
      personnel?: string;
      advisors?: string[];
    };
    serviceProduct?: {
      description?: string;
      lifecycle?: string;
      research?: string;
      intellectual_property?: string[];
      suppliers?: string[];
    };
    marketingSales?: {
      strategy?: string;
      pricing?: string;
      advertising?: string;
      distribution?: string;
      salesForecast?: string;
    };
    fundingRequest?: {
      amount?: number;
      purpose?: string;
      terms?: string;
      exitStrategy?: string;
    };
    financialProjections?: {
      revenue?: Array<{ year: number; amount: number }>;
      expenses?: Array<{ year: number; amount: number }>;
      profitLoss?: Array<{ year: number; profit: number }>;
      cashFlow?: Array<{ year: number; flow: number }>;
      balanceSheet?: string;
      assumptions?: string[];
    };
  };
  compliance: {
    sba_compliant?: boolean;
    us_compliant?: boolean;
    last_checked?: string;
    score?: number;
  };
  metadata: {
    created_at: string;
    updated_at: string;
    created_by?: string;
    industry?: string;
    business_type?: string;
    funding_goal?: number;
  };
  tags?: string[];
  collaborators?: string[];
  export_history?: Array<{
    format: string;
    date: string;
    success: boolean;
  }>;
}

const BusinessPlansPage: React.FC = () => {
  const queryClient = useQueryClient();
  
  // State Management
  const [selectedPlan, setSelectedPlan] = useState<BusinessPlan | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  // Dialog States
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [complianceDialogOpen, setComplianceDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [simulatorDialogOpen, setSimulatorDialogOpen] = useState(false);
  const [fundingConfirmDialogOpen, setFundingConfirmDialogOpen] = useState(false);
  const [pendingFundingApplication, setPendingFundingApplication] = useState<{planId: string, fundingSource: string} | null>(null);
  const [wizardStep, setWizardStep] = useState(0);
  const [hasDraft, setHasDraft] = useState(false);
  
  // Check for draft on mount and when dialog closes
  React.useEffect(() => {
    setHasDraft(!!localStorage.getItem('businessPlanDraft'));
  }, [createDialogOpen]);
  
  // Form Data
  const [formData, setFormData] = useState({
    name: '',
    account_id: '',
    priority: 'medium' as 'low' | 'medium' | 'high',
    plan_type: 'growth' as 'bank' | 'growth' | 'investor', // New field
    legal_name: '',
    trading_name: '',
    founded_year: '',
    company_size: '',
    industry: '',
    business_type: '',
    main_email: '',
    main_phone: '',
    funding_goal: 0,
  });
  
  // Comprehensive Business Plan Data
  const [businessPlanData, setBusinessPlanData] = useState({
    executiveSummary: { overview: '', mission: '', vision: '', keyFactors: [] as string[] },
    companyDescription: { history: '', ownership: '', location: '', facilities: '', products: [] as string[], services: [] as string[], competitiveAdvantages: [] as string[] },
    marketAnalysis: { 
      industryOverview: '', 
      targetMarket: '', 
      marketSize: '', 
      marketTrends: '', 
      competitorAnalysis: '', 
      swotAnalysis: { strengths: [] as string[], weaknesses: [] as string[], opportunities: [] as string[], threats: [] as string[] },
      competitors: [] as Array<{ name: string; strengths: string; weaknesses: string; marketShare: string }>
    },
    organizationManagement: { 
      organizationalStructure: '', 
      managementTeam: [] as Array<{ name: string; role: string; experience: string; education: string }>, 
      personnel: '', 
      advisors: [] as string[] 
    },
    serviceProduct: { description: '', lifecycle: '', research: '', intellectual_property: [] as string[], suppliers: [] as string[] },
    marketingSales: { strategy: '', pricing: '', advertising: '', distribution: '', salesForecast: '' },
    fundingRequest: { amount: 0, purpose: '', terms: '', exitStrategy: '' },
    financialProjections: { 
      revenue: [] as Array<{ year: number; amount: number }>, 
      expenses: [] as Array<{ year: number; amount: number }>, 
      profitLoss: [] as Array<{ year: number; profit: number }>, 
      cashFlow: [] as Array<{ month: string; amount: number }>, 
      balanceSheet: '', 
      assumptions: [] as string[] 
    },
  });
  
  const wizardSteps = [
    'Company Info',
    'Executive Summary',
    'Company Description',
    'Market Analysis',
    'Organization',
    'Products/Services',
    'Marketing & Sales',
    'Funding Request',
    'Financial Projections'
  ];
  
  // Load draft from localStorage on mount
  React.useEffect(() => {
    const savedDraft = localStorage.getItem('businessPlanDraft');
    if (savedDraft && !createDialogOpen) {
      try {
        const draft = JSON.parse(savedDraft);
        if (draft.formData) setFormData(draft.formData);
        if (draft.businessPlanData) setBusinessPlanData(draft.businessPlanData);
        if (draft.wizardStep !== undefined) setWizardStep(draft.wizardStep);
      } catch (e) {
        console.error('Failed to load draft:', e);
      }
    }
  }, []);
  
  // Auto-save to localStorage whenever data changes (but not when editing existing plan)
  React.useEffect(() => {
    if (createDialogOpen && !selectedPlan) {
      const draft = {
        formData,
        businessPlanData,
        wizardStep,
        lastSaved: new Date().toISOString()
      };
      localStorage.setItem('businessPlanDraft', JSON.stringify(draft));
    }
  }, [formData, businessPlanData, wizardStep, createDialogOpen, selectedPlan]);
  
  const [complianceData, setComplianceData] = useState<any>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<string>('');

  // AI Assist Mutation
  const aiAssistMutation = useMutation({
    mutationFn: async (payload: { section: string; context: any }) => {
      setAiLoading(true);
      const response: any = await api.post('/business-plans/ai-assist', payload);
      return response.data;
    },
    onSuccess: (data) => {
      setAiSuggestion(data.suggestion);
      setAiLoading(false);
    },
    onError: (error: any) => {
      console.error('AI assist error:', error);
      alert('AI assistance temporarily unavailable. Please try again.');
      setAiLoading(false);
    },
  });

  // Fetch Business Plans
  const { data: plans = [], isLoading, error, refetch } = useQuery({
    queryKey: ['business-plans'],
    queryFn: async (): Promise<BusinessPlan[]> => {
      try {
        console.log('🔍 Fetching business plans from API...');
        const response: any = await api.get('/business-plans/business-plans');
        console.log('✅ Business plans response:', response);
        
        // Handle different response structures
        // Check if response itself is an array (direct axios response)
        if (Array.isArray(response)) {
          console.log(`✅ Found ${response.length} business plan(s) (direct array)`);
          return response;
        }
        
        if (response?.data) {
          if (Array.isArray(response.data)) {
            console.log(`✅ Found ${response.data.length} business plan(s)`);
            return response.data;
          }
          if (response.data.data && Array.isArray(response.data.data)) {
            console.log(`✅ Found ${response.data.data.length} business plan(s) in nested data`);
            return response.data.data;
          }
          if (response.data.plans && Array.isArray(response.data.plans)) {
            return response.data.plans;
          }
        }
        
        console.warn('⚠️ No data in response, returning empty array');
        return [];
      } catch (error: any) {
        console.error('❌ Error fetching business plans:', error);
        if (error?.response?.status === 404) {
          console.log('ℹ️ No business plans found (404)');
          return [];
        }
        throw new Error(error?.response?.data?.detail || error.message || 'Failed to fetch business plans');
      }
    },
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 2;
    },
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
  });

  // Create Business Plan Mutation
  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload = {
        name: data.name,
        account_id: data.account_id || undefined, // Let backend generate if empty
        priority: data.priority,
        data: businessPlanData,
        compliance: { sba_compliant: false, us_compliant: false },
        metadata: {
          legal_name: data.legal_name,
          trading_name: data.trading_name,
          founded_year: data.founded_year ? parseInt(data.founded_year) : undefined,
          company_size: data.company_size,
          main_email: data.main_email,
          main_phone: data.main_phone,
          industry: data.industry,
          business_type: data.business_type,
          funding_goal: data.funding_goal,
        },
        tags: [],
        collaborators: [],
      };
      
      // Check if we're editing an existing plan
      if (selectedPlan) {
        console.log('🔄 Updating business plan:', selectedPlan.id);
        const response: any = await api.put(`/business-plans/business-plans/${selectedPlan.id}`, payload);
        return response.data;
      } else {
        console.log('🚀 Creating business plan with payload:', payload);
        const response: any = await api.post('/business-plans/business-plans', payload);
        return response.data;
      }
    },
    onSuccess: async (data) => {
      const action = selectedPlan ? 'updated' : 'created';
      console.log(`✅ Business plan ${action} successfully:`, data);
      
      // Close dialog and reset form first
      setCreateDialogOpen(false);
      resetForm();
      setSelectedPlan(null); // Clear selected plan
      
      // Show success message
      alert(`✅ Business plan "${data.name}" ${action} successfully!\n\nPlan ID: ${data.id}\n\nRefreshing list...`);
      
      // Small delay to ensure backend has committed
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Invalidate queries to refresh the list
      await queryClient.invalidateQueries({ queryKey: ['business-plans'] });
      
      // Force a refetch
      refetch();
      
      // Optional: Auto-trigger PDF export only for new plans
      if (!selectedPlan && data && data.id) {
        try {
          console.log('📄 Triggering PDF export for plan:', data.id);
          const exportResponse: any = await api.post(`/business-plans/business-plans/${data.id}/export?format=pdf`);
          console.log('✅ PDF export initiated:', exportResponse.data);
        } catch (error) {
          console.error('⚠️ PDF export failed:', error);
          // Don't fail the whole operation if PDF export fails
        }
      }
    },
    onError: (error: any) => {
      const action = selectedPlan ? 'update' : 'create';
      console.error(`❌ Error ${action}ing business plan:`, error);
      const errorMessage = error?.response?.data?.detail || error.message || 'Unknown error occurred';
      alert(`Failed to ${action} business plan: ${errorMessage}`);
    },
  });

  // Update Business Plan Mutation (placeholder for future use)
  // const updateMutation = useMutation({ ... });

  // Delete Business Plan Mutation
  const deleteMutation = useMutation({
    mutationFn: async (planId: string) => {
      console.log('🗑️ Deleting business plan:', planId);
      const response: any = await api.delete(`/business-plans/business-plans/${planId}`);
      return response.data;
    },
    onSuccess: () => {
      console.log('✅ Business plan deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['business-plans'] });
      setAnchorEl(null);
      setSelectedPlan(null);
    },
    onError: (error: any) => {
      console.error('❌ Error deleting business plan:', error);
      alert(`Failed to delete business plan: ${error?.response?.data?.detail || error.message}`);
    },
  });

  // Export Business Plan Mutation
  const exportMutation = useMutation({
    mutationFn: async ({ planId, format }: { planId: string; format: string }) => {
      console.log('📤 Exporting business plan:', planId, format);
      
      if (format === 'pdf' || format === 'docx') {
        // For PDF and DOCX, we need to handle binary data
        const auth = (await import('../config/firebase')).auth;
        const token = await auth.currentUser?.getIdToken();
        
        const response = await fetch(`/api/v1/business-plans/business-plans/${planId}/export?format=${format}`, {
          method: 'POST',
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
          },
        });
        
        if (!response.ok) {
          const error = await response.text();
          throw new Error(error || `Failed to export ${format.toUpperCase()}`);
        }
        
        // Get the filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        const filenameMatch = contentDisposition?.match(/filename=(.+)/);
        const filename = filenameMatch ? filenameMatch[1] : `business_plan.${format}`;
        
        // Create blob and download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        return { message: `${format.toUpperCase()} downloaded successfully` };
      } else {
        // For JSON, use regular API call
        const response: any = await api.post(`/business-plans/business-plans/${planId}/export?format=${format}`);
        return response.data || response;
      }
    },
    onSuccess: (data: any) => {
      console.log('✅ Export initiated:', data);
      alert(`Export successful: ${data?.message || 'Export completed'}`);
      setExportDialogOpen(false);
      setAnchorEl(null);
    },
    onError: (error: any) => {
      console.error('❌ Export error:', error);
      alert(`Export failed: ${error?.response?.data?.detail || error.message}`);
    },
  });

  // Compliance Check Mutation
  const complianceMutation = useMutation({
    mutationFn: async (planId: string) => {
      console.log('🔍 Running compliance check for plan:', planId);
      const response: any = await api.post(`/business-plans/business-plans/${planId}/compliance-check`);
      return response.data;
    },
    onSuccess: (data: any) => {
      console.log('✅ Compliance check completed:', data);
      setComplianceData(data);
      setComplianceDialogOpen(true);
      setAnchorEl(null);
    },
    onError: (error: any) => {
      console.error('❌ Compliance check error:', error);
      alert(`Compliance check failed: ${error?.response?.data?.detail || error.message}`);
    },
  });

  // Funding Application Mutation with HITL confirmation
  const fundingMutation = useMutation({
    mutationFn: async ({ planId, fundingSource }: { planId: string; fundingSource: string }) => {
      console.log('💰 Submitting funding application:', planId, fundingSource);
      const response: any = await api.post(
        `/business-plans/business-plans/${planId}/funding-application`,
        {
          funding_source: fundingSource,
          user_confirmed: true,
          terms_accepted: true,
          confirmation_timestamp: new Date().toISOString()
        }
      );
      return response.data;
    },
    onSuccess: (data: any) => {
      console.log('✅ Funding application submitted:', data);
      alert(`${data?.message || 'Application submitted successfully'}\n\nStatus: ${data?.status || 'Pending'}`);
      setAnchorEl(null);
    },
    onError: (error: any) => {
      console.error('❌ Funding application error:', error);
      alert(`Funding application failed: ${error?.response?.data?.detail || error.message}`);
    },
  });

  // Helper Functions
  const resetForm = () => {
    setFormData({
      name: '',
      account_id: '',
      priority: 'medium',
      plan_type: 'growth',
      legal_name: '',
      trading_name: '',
      founded_year: '',
      company_size: '',
      industry: '',
      business_type: '',
      main_email: '',
      main_phone: '',
      funding_goal: 0,
    });
    setBusinessPlanData({
      executiveSummary: { overview: '', mission: '', vision: '', keyFactors: [] },
      companyDescription: { history: '', ownership: '', location: '', facilities: '', products: [], services: [], competitiveAdvantages: [] },
      marketAnalysis: { 
        industryOverview: '', 
        targetMarket: '', 
        marketSize: '', 
        marketTrends: '', 
        competitorAnalysis: '', 
        swotAnalysis: { strengths: [], weaknesses: [], opportunities: [], threats: [] },
        competitors: []
      },
      organizationManagement: { organizationalStructure: '', managementTeam: [], personnel: '', advisors: [] },
      serviceProduct: { description: '', lifecycle: '', research: '', intellectual_property: [], suppliers: [] },
      marketingSales: { strategy: '', pricing: '', advertising: '', distribution: '', salesForecast: '' },
      fundingRequest: { amount: 0, purpose: '', terms: '', exitStrategy: '' },
      financialProjections: { revenue: [], expenses: [], profitLoss: [], cashFlow: [], balanceSheet: '', assumptions: [] },
    });
    setWizardStep(0);
    setHasDraft(false);
    localStorage.removeItem('businessPlanDraft');
  };
  
  const loadDraft = () => {
    const savedDraft = localStorage.getItem('businessPlanDraft');
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        if (draft.formData) setFormData(draft.formData);
        if (draft.businessPlanData) setBusinessPlanData(draft.businessPlanData);
        if (draft.wizardStep !== undefined) setWizardStep(draft.wizardStep);
        setHasDraft(true);
        setCreateDialogOpen(true);
        alert(`Draft loaded from ${new Date(draft.lastSaved).toLocaleString()}`);
      } catch (e) {
        console.error('Failed to load draft:', e);
        alert('Failed to load draft');
      }
    } else {
      alert('No saved draft found');
    }
  };

  const handleCreate = () => {
    if (!formData.name.trim()) {
      alert('Please enter a plan name');
      return;
    }
    if (!formData.legal_name.trim()) {
      alert('Please enter the legal company name');
      return;
    }
    createMutation.mutate(formData);
  };

  const handleEdit = (plan: BusinessPlan) => {
    console.log('🔧 Editing plan:', plan.name, plan);
    
    // Close the menu first
    setAnchorEl(null);
    
    // Set the selected plan for updating first
    setSelectedPlan(plan);
    
    // Load the basic form data
    const formDataToSet = {
      name: plan.name,
      account_id: plan.account_id || '',
      priority: plan.priority || 'medium' as 'low' | 'medium' | 'high',
      plan_type: 'growth' as 'bank' | 'growth' | 'investor',
      legal_name: '',
      trading_name: '',
      founded_year: '',
      company_size: '',
      industry: plan.metadata?.industry || '',
      business_type: plan.metadata?.business_type || '',
      main_email: '',
      main_phone: '',
      funding_goal: plan.metadata?.funding_goal || 0,
    };
    console.log('📝 Setting formData:', formDataToSet);
    
    // Load the comprehensive business plan data
    const businessPlanDataToSet = {
      executiveSummary: {
        overview: plan.data.executiveSummary?.overview || '',
        mission: plan.data.executiveSummary?.mission || '',
        vision: plan.data.executiveSummary?.vision || '',
        keyFactors: plan.data.executiveSummary?.keyFactors || [],
      },
      companyDescription: {
        history: plan.data.companyDescription?.history || '',
        ownership: plan.data.companyDescription?.ownership || '',
        location: plan.data.companyDescription?.location || '',
        facilities: plan.data.companyDescription?.facilities || '',
        products: plan.data.companyDescription?.products || [],
        services: plan.data.companyDescription?.services || [],
        competitiveAdvantages: plan.data.companyDescription?.competitiveAdvantages || [],
      },
      marketAnalysis: {
        industryOverview: plan.data.marketAnalysis?.industryOverview || '',
        targetMarket: plan.data.marketAnalysis?.targetMarket || '',
        marketSize: plan.data.marketAnalysis?.marketSize || '',
        marketTrends: plan.data.marketAnalysis?.marketTrends || '',
        competitorAnalysis: plan.data.marketAnalysis?.competitorAnalysis || '',
        swotAnalysis: {
          strengths: plan.data.marketAnalysis?.swotAnalysis?.strengths || [],
          weaknesses: plan.data.marketAnalysis?.swotAnalysis?.weaknesses || [],
          opportunities: plan.data.marketAnalysis?.swotAnalysis?.opportunities || [],
          threats: plan.data.marketAnalysis?.swotAnalysis?.threats || [],
        },
        competitors: [],
      },
      organizationManagement: {
        organizationalStructure: plan.data.organizationManagement?.organizationalStructure || '',
        managementTeam: plan.data.organizationManagement?.managementTeam || [],
        personnel: plan.data.organizationManagement?.personnel || '',
        advisors: plan.data.organizationManagement?.advisors || [],
      },
      serviceProduct: {
        description: plan.data.serviceProduct?.description || '',
        lifecycle: plan.data.serviceProduct?.lifecycle || '',
        research: plan.data.serviceProduct?.research || '',
        intellectual_property: plan.data.serviceProduct?.intellectual_property || [],
        suppliers: plan.data.serviceProduct?.suppliers || [],
      },
      marketingSales: {
        strategy: plan.data.marketingSales?.strategy || '',
        pricing: plan.data.marketingSales?.pricing || '',
        advertising: plan.data.marketingSales?.advertising || '',
        distribution: plan.data.marketingSales?.distribution || '',
        salesForecast: plan.data.marketingSales?.salesForecast || '',
      },
      fundingRequest: {
        amount: plan.data.fundingRequest?.amount || 0,
        purpose: plan.data.fundingRequest?.purpose || '',
        terms: plan.data.fundingRequest?.terms || '',
        exitStrategy: plan.data.fundingRequest?.exitStrategy || '',
      },
      financialProjections: {
        revenue: plan.data.financialProjections?.revenue || [],
        expenses: plan.data.financialProjections?.expenses || [],
        profitLoss: plan.data.financialProjections?.profitLoss || [],
        cashFlow: (plan.data.financialProjections?.cashFlow || []).map((cf: any) => ({
          month: cf.month || `Year ${cf.year || 1}`,
          amount: cf.amount || cf.flow || 0
        })),
        balanceSheet: plan.data.financialProjections?.balanceSheet || '',
        assumptions: plan.data.financialProjections?.assumptions || [],
      },
    };
    console.log('📊 Setting businessPlanData:', businessPlanDataToSet);
    
    // Set both states - replace completely, don't merge
    setFormData(formDataToSet);
    setBusinessPlanData(businessPlanDataToSet);
    
    console.log('🔍 Current formData after set:', formData);
    console.log('🔍 Current businessPlanData after set:', businessPlanData);
    
    // Open dialog immediately - React will batch the state updates
    setWizardStep(0);
    setCreateDialogOpen(true);
    console.log('✅ Dialog opened for editing');
  };

  const handleView = (plan: BusinessPlan) => {
    setSelectedPlan(plan);
    // View functionality could be implemented here
    console.log('View plan:', plan.name);
    setAnchorEl(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, plan: BusinessPlan) => {
    setAnchorEl(event.currentTarget);
    setSelectedPlan(plan);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPlan(null);
  };

  const handleExport = (format: string) => {
    if (selectedPlan) {
      exportMutation.mutate({ planId: selectedPlan.id, format });
    }
  };

  const handleComplianceCheck = () => {
    if (selectedPlan) {
      complianceMutation.mutate(selectedPlan.id);
    }
  };

  const handleFundingApplication = (source: string) => {
    if (selectedPlan) {
      // HITL: Show confirmation dialog before submitting
      setPendingFundingApplication({ planId: selectedPlan.id, fundingSource: source });
      setFundingConfirmDialogOpen(true);
    }
  };

  const handleFundingConfirm = (confirmed: boolean, termsAccepted: boolean) => {
    if (confirmed && termsAccepted && pendingFundingApplication) {
      fundingMutation.mutate(pendingFundingApplication);
      setFundingConfirmDialogOpen(false);
      setPendingFundingApplication(null);
    }
  };

  const handleDelete = (plan: BusinessPlan) => {
    if (window.confirm(`Are you sure you want to delete "${plan.name}"? This action cannot be undone.`)) {
      deleteMutation.mutate(plan.id);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'review': return 'info';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Loading State
  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading Business Plans...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Error State
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Failed to Load Business Plans
          </Typography>
          <Typography variant="body2" gutterBottom>
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </Typography>
          <Button
            variant="contained"
            onClick={() => refetch()}
            sx={{ mt: 2 }}
            startIcon={<Description />}
          >
            Retry
          </Button>
        </Alert>
        <Alert severity="info">
          <Typography variant="body2">
            Check the browser console for detailed error information, or contact support if the issue persists.
          </Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Strategic Business Planning
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Create, manage, and track comprehensive business plans with SBA compliance
          </Typography>
          {plans.length > 0 && (
            <Alert severity="success" sx={{ mt: 2, maxWidth: 600 }}>
              <Typography variant="body2">
                ✅ You have {plans.length} saved business plan{plans.length > 1 ? 's' : ''}. 
                Scroll down to view, edit, or export your plans.
              </Typography>
            </Alert>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            size="large"
            startIcon={<Assessment />}
            onClick={() => setSimulatorDialogOpen(true)}
            sx={{ borderColor: '#9C27B0', color: '#9C27B0' }}
          >
            Financial Simulator
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={<Add />}
            onClick={() => {
              setHasDraft(false);
              resetForm();
              setCreateDialogOpen(true);
            }}
            sx={{
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
            }}
          >
            Create Business Plan
          </Button>
          {localStorage.getItem('businessPlanDraft') && (
            <Button
              variant="outlined"
              size="large"
              startIcon={<Edit />}
              onClick={loadDraft}
              sx={{ borderColor: '#2196F3', color: '#2196F3' }}
            >
              Continue Draft
            </Button>
          )}
        </Box>
      </Box>

      {/* Stats Summary */}
      {plans.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Typography variant="h4">{plans.length}</Typography>
                <Typography variant="body2">Total Plans</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent>
                <Typography variant="h4">
                  {plans.filter(p => p.status === 'completed').length}
                </Typography>
                <Typography variant="body2">Completed</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent>
                <Typography variant="h4">
                  {plans.filter(p => p.compliance?.sba_compliant).length}
                </Typography>
                <Typography variant="body2">SBA Compliant</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
              <CardContent>
                <Typography variant="h4">
                  {Math.round(plans.reduce((acc, p) => acc + p.completion_percentage, 0) / plans.length)}%
                </Typography>
                <Typography variant="body2">Avg. Complete</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Saved Business Plans Section */}
      {plans.length > 0 && (
        <Box mb={3}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description color="primary" />
            Your Saved Business Plans ({plans.length})
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View, edit, export to PDF/Word, or check compliance for your business plans
          </Typography>
        </Box>
      )}

      {/* Business Plans Grid */}
      {plans.length > 0 ? (
        <Grid container spacing={3}>
          {plans.map((plan) => (
            <Grid item xs={12} sm={6} md={4} key={plan.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Header */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Description color="primary" />
                      <Typography variant="h6" noWrap sx={{ maxWidth: 160 }}>
                        {plan.name}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={plan.priority}
                        size="small"
                        color={getPriorityColor(plan.priority) as any}
                        variant="outlined"
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, plan)}
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Completion Progress */}
                  <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="caption" color="text.secondary">
                        Completion
                      </Typography>
                      <Typography variant="caption" fontWeight="bold" color="primary">
                        {plan.completion_percentage.toFixed(0)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={plan.completion_percentage}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          background: `linear-gradient(45deg, ${
                            plan.completion_percentage >= 80 ? '#4caf50' :
                            plan.completion_percentage >= 50 ? '#ff9800' : '#f44336'
                          } 30%, ${
                            plan.completion_percentage >= 80 ? '#8bc34a' :
                            plan.completion_percentage >= 50 ? '#ffb74d' : '#ef5350'
                          } 90%)`,
                        },
                      }}
                    />
                  </Box>

                  {/* Status and Compliance */}
                  <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                    <Chip
                      label={plan.status.replace('_', ' ')}
                      color={getStatusColor(plan.status) as any}
                      size="small"
                    />
                    {plan.compliance?.sba_compliant && (
                      <Chip
                        label="SBA ✓"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {plan.compliance?.us_compliant && (
                      <Chip
                        label="UK ✓"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {plan.metadata?.funding_goal && plan.metadata.funding_goal > 0 && (
                      <Chip
                        label={formatCurrency(plan.metadata.funding_goal)}
                        color="info"
                        size="small"
                        variant="outlined"
                        icon={<AttachMoney fontSize="small" />}
                      />
                    )}
                  </Box>

                  {/* Metadata */}
                  <Typography variant="caption" color="text.secondary" display="block">
                    {plan.metadata?.industry && `Industry: ${plan.metadata.industry}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Updated: {new Date(plan.metadata?.updated_at).toLocaleDateString()}
                  </Typography>
                  {plan.metadata?.business_type && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Type: {plan.metadata.business_type}
                    </Typography>
                  )}
                </CardContent>

                {/* Actions */}
                <CardActions sx={{ pt: 0, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleView(plan)}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Plan">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEdit(plan)}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Check Compliance">
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => {
                          setSelectedPlan(plan);
                          complianceMutation.mutate(plan.id);
                        }}
                        disabled={complianceMutation.isPending}
                      >
                        <CheckCircle />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(plan)}
                        disabled={deleteMutation.isPending}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<FileDownload />}
                    onClick={() => {
                      setSelectedPlan(plan);
                      setExportDialogOpen(true);
                    }}
                    sx={{ ml: 'auto' }}
                  >
                    Export
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        /* Empty State */
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}
        >
          <Business sx={{ fontSize: 80, mb: 2, opacity: 0.8 }} />
          <Typography variant="h4" gutterBottom>
            Start Your Strategic Planning Journey
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
            Create comprehensive business plans with SWOT analysis, market research, and financial projections
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, opacity: 0.8 }}>
            Our platform helps you build SBA-compliant business plans, conduct thorough market analysis,
            and prepare funding applications with confidence.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.3)',
              },
            }}
          >
            Create Your First Business Plan
          </Button>
        </Paper>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <MenuItem onClick={() => handleView(selectedPlan!)}>
          <Visibility sx={{ mr: 2 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => handleEdit(selectedPlan!)}>
          <Edit sx={{ mr: 2 }} />
          Edit Plan
        </MenuItem>
        <MenuItem divider />
        <MenuItem onClick={() => setExportDialogOpen(true)}>
          <FileDownload sx={{ mr: 2 }} />
          Export Options
        </MenuItem>
        <MenuItem onClick={handleComplianceCheck}>
          <CheckCircle sx={{ mr: 2 }} />
          Check Compliance
        </MenuItem>
        <MenuItem divider />
        <MenuItem onClick={() => handleFundingApplication('sba')}>
          <AccountBalance sx={{ mr: 2 }} />
          SBA Application
        </MenuItem>
        <MenuItem onClick={() => handleFundingApplication('sba_lending')}>
          <AttachMoney sx={{ mr: 2 }} />
          SBA Lending
        </MenuItem>
        <MenuItem onClick={() => handleFundingApplication('sba_loans')}>
          <Business sx={{ mr: 2 }} />
          SBA Loans
        </MenuItem>
      </Menu>

      {/* Create Business Plan Wizard Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setWizardStep(0);
          resetForm();
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
            minHeight: '80vh',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <Box flex={1}>
              <Typography variant="h5" component="div" gutterBottom>
                {selectedPlan 
                  ? `✏️ Edit Business Plan: ${selectedPlan.name}` 
                  : hasDraft 
                    ? '📝 Resume Business Plan (Draft)' 
                    : 'Create Comprehensive Business Plan'
                }
              </Typography>
            </Box>
            {formData.plan_type && (
              <Chip 
                label={
                  formData.plan_type === 'bank' ? '🏦 Bank/SBA Loan' : 
                  formData.plan_type === 'investor' ? '💰 Investor Ready' : 
                  '📈 Growth/Operations'
                }
                color={
                  formData.plan_type === 'bank' ? 'primary' : 
                  formData.plan_type === 'investor' ? 'secondary' : 
                  'success'
                }
                sx={{ fontWeight: 'bold' }}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Step {wizardStep + 1} of {wizardSteps.length}: {wizardSteps[wizardStep]}
          </Typography>
          {/* Progress Stepper */}
          <Box sx={{ mt: 2, display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
            {wizardSteps.map((step, index) => (
              <Chip
                key={index}
                label={`${index + 1}. ${step}`}
                size="small"
                color={index === wizardStep ? 'primary' : index < wizardStep ? 'success' : 'default'}
                variant={index === wizardStep ? 'filled' : 'outlined'}
                sx={{ minWidth: 'fit-content' }}
              />
            ))}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ minHeight: '500px' }}>
          <Box sx={{ pt: 2 }}>
            
            {/* Step 0: Company Info */}
            {wizardStep === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Business Plan Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., TechStart Solutions 2024 Business Plan"
                    helperText="Give your business plan a clear, descriptive name"
                  />
                </Grid>
                
                {/* Plan Type Selector */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 1, mb: 2 }}>📋 Business Plan Type</Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>Choose your plan type:</Typography>
                    <Typography variant="caption" display="block">• <strong>Bank/SBA Loan</strong>: Conservative projections, collateral focus, debt service coverage</Typography>
                    <Typography variant="caption" display="block">• <strong>Growth/Operations</strong>: Living document with KPIs, milestones, pivot tracking</Typography>
                    <Typography variant="caption" display="block">• <strong>Investor Ready</strong>: Market size focus, high growth, exit strategy, cap table</Typography>
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Card 
                        sx={{ 
                          p: 2, 
                          cursor: 'pointer',
                          border: formData.plan_type === 'bank' ? '3px solid' : '1px solid',
                          borderColor: formData.plan_type === 'bank' ? 'primary.main' : 'divider',
                          bgcolor: formData.plan_type === 'bank' ? 'primary.light' : 'background.paper',
                          '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
                        }}
                        onClick={() => setFormData({ ...formData, plan_type: 'bank' })}
                      >
                        <Typography variant="h6" gutterBottom>🏦 Bank/SBA Loan</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Conservative projections • Cash flow focus • Collateral documentation • Loan repayment schedule
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card 
                        sx={{ 
                          p: 2, 
                          cursor: 'pointer',
                          border: formData.plan_type === 'growth' ? '3px solid' : '1px solid',
                          borderColor: formData.plan_type === 'growth' ? 'success.main' : 'divider',
                          bgcolor: formData.plan_type === 'growth' ? 'success.light' : 'background.paper',
                          '&:hover': { borderColor: 'success.main', bgcolor: 'action.hover' }
                        }}
                        onClick={() => setFormData({ ...formData, plan_type: 'growth' })}
                      >
                        <Typography variant="h6" gutterBottom>📈 Growth/Operations</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Living document • KPIs & OKRs • Milestone tracking • Pivot management • Team scaling
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card 
                        sx={{ 
                          p: 2, 
                          cursor: 'pointer',
                          border: formData.plan_type === 'investor' ? '3px solid' : '1px solid',
                          borderColor: formData.plan_type === 'investor' ? 'secondary.main' : 'divider',
                          bgcolor: formData.plan_type === 'investor' ? 'secondary.light' : 'background.paper',
                          '&:hover': { borderColor: 'secondary.main', bgcolor: 'action.hover' }
                        }}
                        onClick={() => setFormData({ ...formData, plan_type: 'investor' })}
                      >
                        <Typography variant="h6" gutterBottom>💰 Investor Ready</Typography>
                        <Typography variant="body2" color="text.secondary">
                          TAM/SAM/SOM • High growth • Traction metrics • Cap table • Exit strategy • Pitch deck
                        </Typography>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
                
                <Grid item xs={12}><Typography variant="h6" sx={{ mt: 1 }}>Company Information</Typography></Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Legal Name" value={formData.legal_name} onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })} required placeholder="e.g., TechStart Solutions Inc." helperText="Official registered company name" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Trading Name (Optional)" value={formData.trading_name} onChange={(e) => setFormData({ ...formData, trading_name: e.target.value })} placeholder="e.g., TechStart" helperText="Brand or trading name if different" />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Founded Year" type="number" value={formData.founded_year} onChange={(e) => setFormData({ ...formData, founded_year: e.target.value })} placeholder="e.g., 2024" helperText="Year company was established" inputProps={{ min: 1800, max: new Date().getFullYear() }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Company Size</InputLabel>
                    <Select value={formData.company_size} label="Company Size" onChange={(e) => setFormData({ ...formData, company_size: e.target.value })}>
                      <MenuItem value="startup">Startup (1-10 employees)</MenuItem>
                      <MenuItem value="small">Small (11-50 employees)</MenuItem>
                      <MenuItem value="medium">Medium (51-200 employees)</MenuItem>
                      <MenuItem value="large">Large (201-1000 employees)</MenuItem>
                      <MenuItem value="enterprise">Enterprise (1000+ employees)</MenuItem>
                    </Select>
                    <FormHelperText>Approximate number of employees</FormHelperText>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Main Email" type="email" value={formData.main_email} onChange={(e) => setFormData({ ...formData, main_email: e.target.value })} placeholder="contact@company.com" helperText="Primary contact email" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Main Phone" type="tel" value={formData.main_phone} onChange={(e) => setFormData({ ...formData, main_phone: e.target.value })} placeholder="+1 (555) 123-4567" helperText="Primary contact phone" />
                </Grid>
                
                <Grid item xs={12}><Typography variant="h6" sx={{ mt: 2 }}>Plan Details</Typography></Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Priority Level</InputLabel>
                    <Select 
                      value={formData.priority || 'medium'} 
                      label="Priority Level" 
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                    >
                      <MenuItem value="low">Low Priority</MenuItem>
                      <MenuItem value="medium">Medium Priority</MenuItem>
                      <MenuItem value="high">High Priority</MenuItem>
                    </Select>
                    <FormHelperText>Set the urgency level for this plan</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Industry" value={formData.industry} onChange={(e) => setFormData({ ...formData, industry: e.target.value })} placeholder="e.g., Technology, Healthcare, Retail" helperText="Primary industry sector" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Business Type" value={formData.business_type} onChange={(e) => setFormData({ ...formData, business_type: e.target.value })} placeholder="e.g., LLC, Corporation, Partnership" helperText="Legal business structure" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Funding Goal" type="number" value={formData.funding_goal} onChange={(e) => setFormData({ ...formData, funding_goal: Number(e.target.value) })} placeholder="0" helperText={formData.plan_type === 'bank' ? 'Loan amount needed' : formData.plan_type === 'investor' ? 'Investment round size' : 'Funding target'} InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }} />
                </Grid>
                
                {/* Bank-specific fields */}
                {formData.plan_type === 'bank' && (
                  <>
                    <Grid item xs={12}><Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>🏦 Bank/SBA Loan Details</Typography></Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Collateral Value" type="number" placeholder="0" helperText="Total value of collateral offered" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Monthly Revenue" type="number" placeholder="0" helperText="Current monthly revenue" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Personal Credit Score" type="number" placeholder="700" helperText="Owner's personal credit score" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Desired Loan Term" placeholder="e.g., 5 years, 10 years" helperText="Loan repayment period" />
                    </Grid>
                  </>
                )}
                
                {/* Investor-specific fields */}
                {formData.plan_type === 'investor' && (
                  <>
                    <Grid item xs={12}><Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>💰 Investor Details</Typography></Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Current Valuation" type="number" placeholder="0" helperText="Pre-money valuation" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Funding Stage" placeholder="e.g., Seed, Series A, Series B" helperText="Current funding round" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Monthly Burn Rate" type="number" placeholder="0" helperText="Monthly cash consumption" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Runway (months)" type="number" placeholder="12" helperText="Months until funds depleted" />
                    </Grid>
                  </>
                )}
                
                {/* Growth-specific fields */}
                {formData.plan_type === 'growth' && (
                  <>
                    <Grid item xs={12}><Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>📈 Growth Tracking</Typography></Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Current MRR/ARR" type="number" placeholder="0" helperText="Monthly/Annual Recurring Revenue" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Growth Rate (%)" type="number" placeholder="0" helperText="Month-over-month growth %" InputProps={{ endAdornment: <Typography sx={{ ml: 1 }}>%</Typography> }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Customer Count" type="number" placeholder="0" helperText="Total active customers" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Team Size" type="number" placeholder="0" helperText="Current number of employees" />
                    </Grid>
                  </>
                )}
              </Grid>
            )}
            
            {/* Step 1: Executive Summary */}
            {wizardStep === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h6" gutterBottom>Executive Summary</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Provide a high-level overview of your business plan. This section should be concise but compelling.
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={aiLoading ? <CircularProgress size={16} /> : <Add />}
                      disabled={aiLoading || !formData.name || !formData.industry}
                      onClick={() => {
                        aiAssistMutation.mutate({
                          section: 'executive_summary',
                          context: {
                            company_name: formData.name,
                            industry: formData.industry,
                            business_type: formData.business_type,
                            mission: businessPlanData.executiveSummary.mission
                          }
                        });
                      }}
                      sx={{
                        background: 'linear-gradient(45deg, #9C27B0 30%, #E91E63 90%)',
                        color: 'white'
                      }}
                    >
                      ✨ AI Assist
                    </Button>
                  </Box>
                </Grid>
                
                {/* AI Suggestion Display */}
                {aiSuggestion && (
                  <Grid item xs={12}>
                    <Alert 
                      severity="info" 
                      onClose={() => setAiSuggestion('')}
                      action={
                        <Button 
                          size="small" 
                          onClick={() => {
                            setBusinessPlanData({
                              ...businessPlanData,
                              executiveSummary: {
                                ...businessPlanData.executiveSummary,
                                overview: aiSuggestion
                              }
                            });
                            setAiSuggestion('');
                          }}
                        >
                          Use This
                        </Button>
                      }
                    >
                      <Typography variant="subtitle2" fontWeight="bold">AI Suggestion:</Typography>
                      <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                        {aiSuggestion}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Business Overview"
                    value={businessPlanData.executiveSummary.overview}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, executiveSummary: { ...businessPlanData.executiveSummary, overview: e.target.value } })}
                    placeholder="Describe your business, what it does, and why it's unique..."
                    helperText="A brief summary of your business concept and value proposition"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Mission Statement"
                    value={businessPlanData.executiveSummary.mission}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, executiveSummary: { ...businessPlanData.executiveSummary, mission: e.target.value } })}
                    placeholder="What is your company's purpose and primary objectives?"
                    helperText="Your company's reason for existing"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Vision Statement"
                    value={businessPlanData.executiveSummary.vision}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, executiveSummary: { ...businessPlanData.executiveSummary, vision: e.target.value } })}
                    placeholder="Where do you see your company in 5-10 years?"
                    helperText="Your company's long-term aspirations"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Key Success Factors</Typography>
                  <TextField
                    fullWidth
                    placeholder="Add key factors that will drive success (press Enter to add each one)"
                    helperText="List the critical elements that will make your business successful"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const target = e.target as HTMLInputElement;
                        const value = target.value.trim();
                        if (value) {
                          setBusinessPlanData({ 
                            ...businessPlanData, 
                            executiveSummary: { 
                              ...businessPlanData.executiveSummary, 
                              keyFactors: [...businessPlanData.executiveSummary.keyFactors, value] 
                            } 
                          });
                          target.value = '';
                        }
                        e.preventDefault();
                      }
                    }}
                  />
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {businessPlanData.executiveSummary.keyFactors.map((factor, index) => (
                      <Chip
                        key={index}
                        label={factor}
                        onDelete={() => {
                          setBusinessPlanData({
                            ...businessPlanData,
                            executiveSummary: {
                              ...businessPlanData.executiveSummary,
                              keyFactors: businessPlanData.executiveSummary.keyFactors.filter((_, i) => i !== index)
                            }
                          });
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            )}
            
            {/* Step 2: Company Description */}
            {wizardStep === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Company Description</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Tell the story of your company - its history, structure, and what makes it special.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Company History"
                    value={businessPlanData.companyDescription.history}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, companyDescription: { ...businessPlanData.companyDescription, history: e.target.value } })}
                    placeholder="How did your company start? What milestones have you achieved?"
                    helperText="The story of how your company came to be"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Ownership Structure"
                    value={businessPlanData.companyDescription.ownership}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, companyDescription: { ...businessPlanData.companyDescription, ownership: e.target.value } })}
                    placeholder="Who owns the company and what are their stakes?"
                    helperText="Ownership breakdown and equity distribution"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Location & Facilities"
                    value={businessPlanData.companyDescription.location}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, companyDescription: { ...businessPlanData.companyDescription, location: e.target.value } })}
                    placeholder="Where are you located? What facilities do you have?"
                    helperText="Physical presence and infrastructure"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Products/Services Offered</Typography>
                  <TextField
                    fullWidth
                    placeholder="Add products or services (press Enter to add)"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const target = e.target as HTMLInputElement;
                        const value = target.value.trim();
                        if (value) {
                          setBusinessPlanData({ 
                            ...businessPlanData, 
                            companyDescription: { 
                              ...businessPlanData.companyDescription, 
                              products: [...businessPlanData.companyDescription.products, value] 
                            } 
                          });
                          target.value = '';
                        }
                        e.preventDefault();
                      }
                    }}
                  />
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {businessPlanData.companyDescription.products.map((product, index) => (
                      <Chip
                        key={index}
                        label={product}
                        color="primary"
                        variant="outlined"
                        onDelete={() => {
                          setBusinessPlanData({
                            ...businessPlanData,
                            companyDescription: {
                              ...businessPlanData.companyDescription,
                              products: businessPlanData.companyDescription.products.filter((_, i) => i !== index)
                            }
                          });
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Competitive Advantages</Typography>
                  <TextField
                    fullWidth
                    placeholder="Add competitive advantages (press Enter to add)"
                    helperText="What sets you apart from competitors?"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const target = e.target as HTMLInputElement;
                        const value = target.value.trim();
                        if (value) {
                          setBusinessPlanData({ 
                            ...businessPlanData, 
                            companyDescription: { 
                              ...businessPlanData.companyDescription, 
                              competitiveAdvantages: [...businessPlanData.companyDescription.competitiveAdvantages, value] 
                            } 
                          });
                          target.value = '';
                        }
                        e.preventDefault();
                      }
                    }}
                  />
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {businessPlanData.companyDescription.competitiveAdvantages.map((advantage, index) => (
                      <Chip
                        key={index}
                        label={advantage}
                        color="success"
                        variant="outlined"
                        onDelete={() => {
                          setBusinessPlanData({
                            ...businessPlanData,
                            companyDescription: {
                              ...businessPlanData.companyDescription,
                              competitiveAdvantages: businessPlanData.companyDescription.competitiveAdvantages.filter((_, i) => i !== index)
                            }
                          });
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            )}
            
            {/* Step 3: Market Analysis with SWOT */}
            {wizardStep === 3 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                    <Box>
                      <Typography variant="h6" gutterBottom>Market Analysis</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Analyze your market, competitors, and perform a SWOT analysis.
                      </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={aiLoading ? <CircularProgress size={16} /> : <Add />}
                        disabled={aiLoading || !formData.industry}
                        onClick={() => {
                          aiAssistMutation.mutate({
                            section: 'market_analysis',
                            context: {
                              industry: formData.industry,
                              target_market: businessPlanData.marketAnalysis.targetMarket,
                              location: businessPlanData.companyDescription.location
                            }
                          });
                        }}
                        sx={{ background: 'linear-gradient(45deg, #9C27B0 30%, #E91E63 90%)' }}
                      >
                        ✨ AI Market Research
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={aiLoading ? <CircularProgress size={16} /> : <Add />}
                        disabled={aiLoading || !formData.name}
                        onClick={() => {
                          aiAssistMutation.mutate({
                            section: 'swot_analysis',
                            context: {
                              company_name: formData.name,
                              industry: formData.industry,
                              description: businessPlanData.companyDescription.history,
                              competitors: businessPlanData.marketAnalysis.competitorAnalysis
                            }
                          });
                        }}
                        sx={{ borderColor: '#9C27B0', color: '#9C27B0' }}
                      >
                        🎯 AI SWOT
                      </Button>
                    </Box>
                  </Box>
                </Grid>
                
                {/* AI Suggestion Display */}
                {aiSuggestion && (
                  <Grid item xs={12}>
                    <Alert 
                      severity="info" 
                      onClose={() => setAiSuggestion('')}
                      sx={{ maxHeight: '300px', overflow: 'auto' }}
                    >
                      <Typography variant="subtitle2" fontWeight="bold">AI Insight:</Typography>
                      <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                        {aiSuggestion}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Industry Overview"
                    value={businessPlanData.marketAnalysis.industryOverview}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, marketAnalysis: { ...businessPlanData.marketAnalysis, industryOverview: e.target.value } })}
                    placeholder="Describe the overall industry landscape..."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Target Market"
                    value={businessPlanData.marketAnalysis.targetMarket}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, marketAnalysis: { ...businessPlanData.marketAnalysis, targetMarket: e.target.value } })}
                    placeholder="Who are your ideal customers?"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Market Size & Growth"
                    value={businessPlanData.marketAnalysis.marketSize}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, marketAnalysis: { ...businessPlanData.marketAnalysis, marketSize: e.target.value } })}
                    placeholder="What is the total addressable market?"
                  />
                </Grid>
                
                {/* SWOT Analysis */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>SWOT Analysis</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Strengths</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Add strength (press Enter)"
                      sx={{ bgcolor: 'white', mb: 1 }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          const target = e.target as HTMLInputElement;
                          const value = target.value.trim();
                          if (value) {
                            setBusinessPlanData({ 
                              ...businessPlanData, 
                              marketAnalysis: { 
                                ...businessPlanData.marketAnalysis, 
                                swotAnalysis: {
                                  ...businessPlanData.marketAnalysis.swotAnalysis,
                                  strengths: [...businessPlanData.marketAnalysis.swotAnalysis.strengths, value]
                                }
                              } 
                            });
                            target.value = '';
                          }
                          e.preventDefault();
                        }
                      }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {businessPlanData.marketAnalysis.swotAnalysis.strengths.map((item, index) => (
                        <Chip
                          key={index}
                          label={item}
                          size="small"
                          onDelete={() => {
                            setBusinessPlanData({
                              ...businessPlanData,
                              marketAnalysis: {
                                ...businessPlanData.marketAnalysis,
                                swotAnalysis: {
                                  ...businessPlanData.marketAnalysis.swotAnalysis,
                                  strengths: businessPlanData.marketAnalysis.swotAnalysis.strengths.filter((_, i) => i !== index)
                                }
                              }
                            });
                          }}
                        />
                      ))}
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Weaknesses</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Add weakness (press Enter)"
                      sx={{ bgcolor: 'white', mb: 1 }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          const target = e.target as HTMLInputElement;
                          const value = target.value.trim();
                          if (value) {
                            setBusinessPlanData({ 
                              ...businessPlanData, 
                              marketAnalysis: { 
                                ...businessPlanData.marketAnalysis, 
                                swotAnalysis: {
                                  ...businessPlanData.marketAnalysis.swotAnalysis,
                                  weaknesses: [...businessPlanData.marketAnalysis.swotAnalysis.weaknesses, value]
                                }
                              } 
                            });
                            target.value = '';
                          }
                          e.preventDefault();
                        }
                      }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {businessPlanData.marketAnalysis.swotAnalysis.weaknesses.map((item, index) => (
                        <Chip
                          key={index}
                          label={item}
                          size="small"
                          onDelete={() => {
                            setBusinessPlanData({
                              ...businessPlanData,
                              marketAnalysis: {
                                ...businessPlanData.marketAnalysis,
                                swotAnalysis: {
                                  ...businessPlanData.marketAnalysis.swotAnalysis,
                                  weaknesses: businessPlanData.marketAnalysis.swotAnalysis.weaknesses.filter((_, i) => i !== index)
                                }
                              }
                            });
                          }}
                        />
                      ))}
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Opportunities</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Add opportunity (press Enter)"
                      sx={{ bgcolor: 'white', mb: 1 }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          const target = e.target as HTMLInputElement;
                          const value = target.value.trim();
                          if (value) {
                            setBusinessPlanData({ 
                              ...businessPlanData, 
                              marketAnalysis: { 
                                ...businessPlanData.marketAnalysis, 
                                swotAnalysis: {
                                  ...businessPlanData.marketAnalysis.swotAnalysis,
                                  opportunities: [...businessPlanData.marketAnalysis.swotAnalysis.opportunities, value]
                                }
                              } 
                            });
                            target.value = '';
                          }
                          e.preventDefault();
                        }
                      }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {businessPlanData.marketAnalysis.swotAnalysis.opportunities.map((item, index) => (
                        <Chip
                          key={index}
                          label={item}
                          size="small"
                          onDelete={() => {
                            setBusinessPlanData({
                              ...businessPlanData,
                              marketAnalysis: {
                                ...businessPlanData.marketAnalysis,
                                swotAnalysis: {
                                  ...businessPlanData.marketAnalysis.swotAnalysis,
                                  opportunities: businessPlanData.marketAnalysis.swotAnalysis.opportunities.filter((_, i) => i !== index)
                                }
                              }
                            });
                          }}
                        />
                      ))}
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Threats</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Add threat (press Enter)"
                      sx={{ bgcolor: 'white', mb: 1 }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          const target = e.target as HTMLInputElement;
                          const value = target.value.trim();
                          if (value) {
                            setBusinessPlanData({ 
                              ...businessPlanData, 
                              marketAnalysis: { 
                                ...businessPlanData.marketAnalysis, 
                                swotAnalysis: {
                                  ...businessPlanData.marketAnalysis.swotAnalysis,
                                  threats: [...businessPlanData.marketAnalysis.swotAnalysis.threats, value]
                                }
                              } 
                            });
                            target.value = '';
                          }
                          e.preventDefault();
                        }
                      }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {businessPlanData.marketAnalysis.swotAnalysis.threats.map((item, index) => (
                        <Chip
                          key={index}
                          label={item}
                          size="small"
                          onDelete={() => {
                            setBusinessPlanData({
                              ...businessPlanData,
                              marketAnalysis: {
                                ...businessPlanData.marketAnalysis,
                                swotAnalysis: {
                                  ...businessPlanData.marketAnalysis.swotAnalysis,
                                  threats: businessPlanData.marketAnalysis.swotAnalysis.threats.filter((_, i) => i !== index)
                                }
                              }
                            });
                          }}
                        />
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            )}
            
            {/* Step 4: Organization & Management */}
            {wizardStep === 4 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Organization & Management</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Describe your organizational structure and key team members.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Organizational Structure"
                    value={businessPlanData.organizationManagement.organizationalStructure}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, organizationManagement: { ...businessPlanData.organizationManagement, organizationalStructure: e.target.value } })}
                    placeholder="Describe your company's organizational hierarchy, departments, reporting structure..."
                    helperText="How is your company organized?"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Personnel Plan"
                    value={businessPlanData.organizationManagement.personnel}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, organizationManagement: { ...businessPlanData.organizationManagement, personnel: e.target.value } })}
                    placeholder="Current and future staffing needs, hiring plans..."
                    helperText="Your hiring strategy and personnel requirements"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>Management Team</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Name" id="mgmt-name" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Role/Title" id="mgmt-role" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Experience" id="mgmt-exp" placeholder="Years of relevant experience" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Education" id="mgmt-edu" placeholder="Highest degree/certification" />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<Add />}
                          onClick={() => {
                            const name = (document.getElementById('mgmt-name') as HTMLInputElement).value;
                            const role = (document.getElementById('mgmt-role') as HTMLInputElement).value;
                            const experience = (document.getElementById('mgmt-exp') as HTMLInputElement).value;
                            const education = (document.getElementById('mgmt-edu') as HTMLInputElement).value;
                            if (name && role) {
                              setBusinessPlanData({
                                ...businessPlanData,
                                organizationManagement: {
                                  ...businessPlanData.organizationManagement,
                                  managementTeam: [...businessPlanData.organizationManagement.managementTeam, { name, role, experience, education }]
                                }
                              });
                              (document.getElementById('mgmt-name') as HTMLInputElement).value = '';
                              (document.getElementById('mgmt-role') as HTMLInputElement).value = '';
                              (document.getElementById('mgmt-exp') as HTMLInputElement).value = '';
                              (document.getElementById('mgmt-edu') as HTMLInputElement).value = '';
                            }
                          }}
                        >
                          Add Team Member
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                  <Box sx={{ mt: 2 }}>
                    {businessPlanData.organizationManagement.managementTeam.map((member, index) => (
                      <Card key={index} sx={{ mb: 1, p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">{member.name}</Typography>
                            <Typography variant="body2" color="primary">{member.role}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {member.experience && `${member.experience} experience`}
                              {member.experience && member.education && ' • '}
                              {member.education}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => {
                              setBusinessPlanData({
                                ...businessPlanData,
                                organizationManagement: {
                                  ...businessPlanData.organizationManagement,
                                  managementTeam: businessPlanData.organizationManagement.managementTeam.filter((_, i) => i !== index)
                                }
                              });
                            }}
                          >
                            Remove
                          </Button>
                        </Box>
                      </Card>
                    ))}
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Advisors & Board Members</Typography>
                  <TextField
                    fullWidth
                    placeholder="Add advisor name and role (press Enter)"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const target = e.target as HTMLInputElement;
                        const value = target.value.trim();
                        if (value) {
                          setBusinessPlanData({
                            ...businessPlanData,
                            organizationManagement: {
                              ...businessPlanData.organizationManagement,
                              advisors: [...businessPlanData.organizationManagement.advisors, value]
                            }
                          });
                          target.value = '';
                        }
                        e.preventDefault();
                      }
                    }}
                  />
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {businessPlanData.organizationManagement.advisors.map((advisor, index) => (
                      <Chip
                        key={index}
                        label={advisor}
                        color="secondary"
                        variant="outlined"
                        onDelete={() => {
                          setBusinessPlanData({
                            ...businessPlanData,
                            organizationManagement: {
                              ...businessPlanData.organizationManagement,
                              advisors: businessPlanData.organizationManagement.advisors.filter((_, i) => i !== index)
                            }
                          });
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            )}
            
            {/* Step 5: Products/Services */}
            {wizardStep === 5 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Products & Services</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Detail your products or services, their lifecycle, and competitive advantages.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    label="Product/Service Description"
                    value={businessPlanData.serviceProduct.description}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, serviceProduct: { ...businessPlanData.serviceProduct, description: e.target.value } })}
                    placeholder="Describe your products or services in detail. What problems do they solve? What value do they provide?"
                    helperText="Comprehensive description of what you offer"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Product Lifecycle"
                    value={businessPlanData.serviceProduct.lifecycle}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, serviceProduct: { ...businessPlanData.serviceProduct, lifecycle: e.target.value } })}
                    placeholder="Development stage, production cycle, maintenance..."
                    helperText="Where is your product in its lifecycle?"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Research & Development"
                    value={businessPlanData.serviceProduct.research}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, serviceProduct: { ...businessPlanData.serviceProduct, research: e.target.value } })}
                    placeholder="Ongoing R&D efforts, innovation pipeline, future products..."
                    helperText="Your innovation and development strategy"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Intellectual Property</Typography>
                  <TextField
                    fullWidth
                    placeholder="Add patents, trademarks, copyrights (press Enter)"
                    helperText="List any IP assets you own or have pending"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const target = e.target as HTMLInputElement;
                        const value = target.value.trim();
                        if (value) {
                          setBusinessPlanData({
                            ...businessPlanData,
                            serviceProduct: {
                              ...businessPlanData.serviceProduct,
                              intellectual_property: [...businessPlanData.serviceProduct.intellectual_property, value]
                            }
                          });
                          target.value = '';
                        }
                        e.preventDefault();
                      }
                    }}
                  />
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {businessPlanData.serviceProduct.intellectual_property.map((ip, index) => (
                      <Chip
                        key={index}
                        label={ip}
                        color="info"
                        variant="outlined"
                        icon={<Business />}
                        onDelete={() => {
                          setBusinessPlanData({
                            ...businessPlanData,
                            serviceProduct: {
                              ...businessPlanData.serviceProduct,
                              intellectual_property: businessPlanData.serviceProduct.intellectual_property.filter((_, i) => i !== index)
                            }
                          });
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Key Suppliers & Partners</Typography>
                  <TextField
                    fullWidth
                    placeholder="Add supplier or partner name (press Enter)"
                    helperText="Important vendors, manufacturers, or strategic partners"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const target = e.target as HTMLInputElement;
                        const value = target.value.trim();
                        if (value) {
                          setBusinessPlanData({
                            ...businessPlanData,
                            serviceProduct: {
                              ...businessPlanData.serviceProduct,
                              suppliers: [...businessPlanData.serviceProduct.suppliers, value]
                            }
                          });
                          target.value = '';
                        }
                        e.preventDefault();
                      }
                    }}
                  />
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {businessPlanData.serviceProduct.suppliers.map((supplier, index) => (
                      <Chip
                        key={index}
                        label={supplier}
                        variant="outlined"
                        onDelete={() => {
                          setBusinessPlanData({
                            ...businessPlanData,
                            serviceProduct: {
                              ...businessPlanData.serviceProduct,
                              suppliers: businessPlanData.serviceProduct.suppliers.filter((_, i) => i !== index)
                            }
                          });
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            )}
            
            {/* Step 6: Marketing & Sales */}
            {wizardStep === 6 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Marketing & Sales Strategy</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Outline how you'll attract and retain customers.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Marketing Strategy"
                    value={businessPlanData.marketingSales.strategy}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, marketingSales: { ...businessPlanData.marketingSales, strategy: e.target.value } })}
                    placeholder="How will you reach your target market? Digital marketing, content, partnerships, events..."
                    helperText="Your overall marketing approach"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Pricing Strategy"
                    value={businessPlanData.marketingSales.pricing}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, marketingSales: { ...businessPlanData.marketingSales, pricing: e.target.value } })}
                    placeholder="How do you price your products/services? Competitive, premium, freemium..."
                    helperText="Your pricing model and strategy"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Advertising & Promotion"
                    value={businessPlanData.marketingSales.advertising}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, marketingSales: { ...businessPlanData.marketingSales, advertising: e.target.value } })}
                    placeholder="Advertising channels, promotional campaigns, budget allocation..."
                    helperText="How you'll promote your business"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Distribution Channels"
                    value={businessPlanData.marketingSales.distribution}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, marketingSales: { ...businessPlanData.marketingSales, distribution: e.target.value } })}
                    placeholder="Direct sales, online, retail, distributors, partnerships..."
                    helperText="How customers will access your products"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Sales Forecast"
                    value={businessPlanData.marketingSales.salesForecast}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, marketingSales: { ...businessPlanData.marketingSales, salesForecast: e.target.value } })}
                    placeholder="Projected sales for Year 1, 2, 3... Include assumptions and growth rates"
                    helperText="Your sales projections and targets"
                  />
                </Grid>
              </Grid>
            )}
            
            {/* Step 7: Funding Request */}
            {wizardStep === 7 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Funding Request</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Specify your funding needs and how you plan to use the funds.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Funding Amount Requested"
                    type="number"
                    value={businessPlanData.fundingRequest.amount}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, fundingRequest: { ...businessPlanData.fundingRequest, amount: Number(e.target.value) } })}
                    placeholder="0"
                    helperText="Total amount of funding you're seeking (GBP)"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    label="Use of Funds"
                    value={businessPlanData.fundingRequest.purpose}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, fundingRequest: { ...businessPlanData.fundingRequest, purpose: e.target.value } })}
                    placeholder="Detail how you'll use the funding: equipment, inventory, hiring, marketing, R&D, working capital..."
                    helperText="Breakdown of how funds will be allocated"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Funding Terms"
                    value={businessPlanData.fundingRequest.terms}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, fundingRequest: { ...businessPlanData.fundingRequest, terms: e.target.value } })}
                    placeholder="Type of funding (equity, debt, convertible note), terms, timeframe, interest rate..."
                    helperText="Preferred funding structure"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Exit Strategy"
                    value={businessPlanData.fundingRequest.exitStrategy}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, fundingRequest: { ...businessPlanData.fundingRequest, exitStrategy: e.target.value } })}
                    placeholder="How will investors realize returns? Acquisition, IPO, buyback, dividend strategy..."
                    helperText="Plan for investor returns"
                  />
                </Grid>
              </Grid>
            )}
            
            {/* Step 8: Financial Projections */}
            {wizardStep === 8 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h6" gutterBottom>Financial Projections</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Provide financial forecasts and assumptions for the next 3-5 years with automatic scenario analysis.
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={aiLoading ? <CircularProgress size={16} /> : <Add />}
                      disabled={aiLoading || !formData.industry}
                      onClick={() => {
                        aiAssistMutation.mutate({
                          section: 'financial_projections',
                          context: {
                            industry: formData.industry,
                            business_type: formData.business_type,
                            company_size: formData.company_size,
                            funding_goal: formData.funding_goal
                          }
                        });
                      }}
                      sx={{ background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)' }}
                    >
                      💰 AI Financial Forecast
                    </Button>
                  </Box>
                </Grid>
                
                {/* AI Suggestion Display */}
                {aiSuggestion && (
                  <Grid item xs={12}>
                    <Alert 
                      severity="success" 
                      onClose={() => setAiSuggestion('')}
                      sx={{ maxHeight: '300px', overflow: 'auto' }}
                    >
                      <Typography variant="subtitle2" fontWeight="bold">AI Financial Recommendations:</Typography>
                      <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                        {aiSuggestion}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Add year-by-year projections. The system will automatically calculate profit/loss and generate Best Case (+20%), Realistic Case, and Worst Case (-20%) scenarios.
                    </Typography>
                  </Alert>
                </Grid>
                
                {/* Revenue Projections */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Revenue Projections</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField fullWidth size="small" label="Year" type="number" id="rev-year" placeholder="2024" />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField fullWidth size="small" label="Revenue ($)" type="number" id="rev-amount" placeholder="100000" />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<Add />}
                          onClick={() => {
                            const year = Number((document.getElementById('rev-year') as HTMLInputElement).value);
                            const amount = Number((document.getElementById('rev-amount') as HTMLInputElement).value);
                            if (year && amount) {
                              setBusinessPlanData({
                                ...businessPlanData,
                                financialProjections: {
                                  ...businessPlanData.financialProjections,
                                  revenue: [...businessPlanData.financialProjections.revenue, { year, amount }]
                                }
                              });
                              (document.getElementById('rev-year') as HTMLInputElement).value = '';
                              (document.getElementById('rev-amount') as HTMLInputElement).value = '';
                            }
                          }}
                        >
                          Add Revenue Entry
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                  <Box sx={{ mt: 1 }}>
                    {businessPlanData.financialProjections.revenue.map((item, index) => (
                      <Chip
                        key={index}
                        label={`Year ${item.year}: $${item.amount.toLocaleString()}`}
                        onDelete={() => {
                          setBusinessPlanData({
                            ...businessPlanData,
                            financialProjections: {
                              ...businessPlanData.financialProjections,
                              revenue: businessPlanData.financialProjections.revenue.filter((_, i) => i !== index)
                            }
                          });
                        }}
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                </Grid>
                
                {/* Expenses Projections */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Expense Projections</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField fullWidth size="small" label="Year" type="number" id="exp-year" placeholder="2024" />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField fullWidth size="small" label="Expenses ($)" type="number" id="exp-amount" placeholder="50000" />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<Add />}
                          onClick={() => {
                            const year = Number((document.getElementById('exp-year') as HTMLInputElement).value);
                            const amount = Number((document.getElementById('exp-amount') as HTMLInputElement).value);
                            if (year && amount) {
                              setBusinessPlanData({
                                ...businessPlanData,
                                financialProjections: {
                                  ...businessPlanData.financialProjections,
                                  expenses: [...businessPlanData.financialProjections.expenses, { year, amount }]
                                }
                              });
                              (document.getElementById('exp-year') as HTMLInputElement).value = '';
                              (document.getElementById('exp-amount') as HTMLInputElement).value = '';
                            }
                          }}
                        >
                          Add Expense Entry
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                  <Box sx={{ mt: 1 }}>
                    {businessPlanData.financialProjections.expenses.map((item, index) => (
                      <Chip
                        key={index}
                        label={`Year ${item.year}: $${item.amount.toLocaleString()}`}
                        color="warning"
                        variant="outlined"
                        onDelete={() => {
                          setBusinessPlanData({
                            ...businessPlanData,
                            financialProjections: {
                              ...businessPlanData.financialProjections,
                              expenses: businessPlanData.financialProjections.expenses.filter((_, i) => i !== index)
                            }
                          });
                        }}
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                </Grid>
                
                {/* Automatic Calculations & Scenarios */}
                {(businessPlanData.financialProjections.revenue.length > 0 || businessPlanData.financialProjections.expenses.length > 0) && (
                  <Grid item xs={12}>
                    <Alert severity="success" icon={<AttachMoney />}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>📊 Automatic Financial Analysis</Typography>
                      <Typography variant="body2" paragraph>
                        Based on your projections, here are the calculated scenarios:
                      </Typography>
                      
                      <Box sx={{ mt: 2 }}>
                        {/* Calculate scenarios for each year */}
                        {Array.from(new Set([
                          ...businessPlanData.financialProjections.revenue.map(r => r.year),
                          ...businessPlanData.financialProjections.expenses.map(e => e.year)
                        ])).sort().map((year) => {
                          const revenue = businessPlanData.financialProjections.revenue.find(r => r.year === year)?.amount || 0;
                          const expenses = businessPlanData.financialProjections.expenses.find(e => e.year === year)?.amount || 0;
                          
                          // Realistic Case (user input)
                          const realisticProfit = revenue - expenses;
                          const realisticMargin = revenue > 0 ? ((realisticProfit / revenue) * 100).toFixed(1) : '0.0';
                          
                          // Best Case (+20% revenue, same expenses)
                          const bestRevenue = revenue * 1.2;
                          const bestProfit = bestRevenue - expenses;
                          const bestMargin = bestRevenue > 0 ? ((bestProfit / bestRevenue) * 100).toFixed(1) : '0.0';
                          
                          // Worst Case (-20% revenue, same expenses)
                          const worstRevenue = revenue * 0.8;
                          const worstProfit = worstRevenue - expenses;
                          const worstMargin = worstRevenue > 0 ? ((worstProfit / worstRevenue) * 100).toFixed(1) : '0.0';
                          
                          return (
                            <Paper key={year} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                Year {year} Analysis
                              </Typography>
                              <Grid container spacing={2}>
                                {/* Best Case */}
                                <Grid item xs={12} md={4}>
                                  <Box sx={{ p: 1.5, bgcolor: 'success.light', borderRadius: 1, border: '2px solid', borderColor: 'success.main' }}>
                                    <Typography variant="caption" fontWeight="bold" color="success.dark">
                                      🎯 BEST CASE (+20%)
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                      Revenue: ${bestRevenue.toLocaleString()}
                                    </Typography>
                                    <Typography variant="body2">
                                      Expenses: ${expenses.toLocaleString()}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="bold" color={bestProfit >= 0 ? 'success.dark' : 'error.main'}>
                                      Profit: ${bestProfit.toLocaleString()} ({bestMargin}%)
                                    </Typography>
                                  </Box>
                                </Grid>
                                
                                {/* Realistic Case */}
                                <Grid item xs={12} md={4}>
                                  <Box sx={{ p: 1.5, bgcolor: 'info.light', borderRadius: 1, border: '2px solid', borderColor: 'info.main' }}>
                                    <Typography variant="caption" fontWeight="bold" color="info.dark">
                                      📈 REALISTIC CASE
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                      Revenue: ${revenue.toLocaleString()}
                                    </Typography>
                                    <Typography variant="body2">
                                      Expenses: ${expenses.toLocaleString()}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="bold" color={realisticProfit >= 0 ? 'success.dark' : 'error.main'}>
                                      Profit: ${realisticProfit.toLocaleString()} ({realisticMargin}%)
                                    </Typography>
                                  </Box>
                                </Grid>
                                
                                {/* Worst Case */}
                                <Grid item xs={12} md={4}>
                                  <Box sx={{ p: 1.5, bgcolor: 'warning.light', borderRadius: 1, border: '2px solid', borderColor: 'warning.main' }}>
                                    <Typography variant="caption" fontWeight="bold" color="warning.dark">
                                      ⚠️ WORST CASE (-20%)
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                      Revenue: ${worstRevenue.toLocaleString()}
                                    </Typography>
                                    <Typography variant="body2">
                                      Expenses: ${expenses.toLocaleString()}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="bold" color={worstProfit >= 0 ? 'success.dark' : 'error.main'}>
                                      Profit: ${worstProfit.toLocaleString()} ({worstMargin}%)
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Paper>
                          );
                        })}
                      </Box>
                    </Alert>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Balance Sheet Summary"
                    value={businessPlanData.financialProjections.balanceSheet}
                    onChange={(e) => setBusinessPlanData({ ...businessPlanData, financialProjections: { ...businessPlanData.financialProjections, balanceSheet: e.target.value } })}
                    placeholder="Summarize your projected assets, liabilities, and equity..."
                    helperText="High-level balance sheet forecast"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Key Financial Assumptions</Typography>
                  <TextField
                    fullWidth
                    placeholder="Add assumption (press Enter) - e.g., 'Customer acquisition cost: $50', 'Monthly churn: 2%'"
                    helperText="List the key assumptions underlying your financial projections"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const target = e.target as HTMLInputElement;
                        const value = target.value.trim();
                        if (value) {
                          setBusinessPlanData({
                            ...businessPlanData,
                            financialProjections: {
                              ...businessPlanData.financialProjections,
                              assumptions: [...businessPlanData.financialProjections.assumptions, value]
                            }
                          });
                          target.value = '';
                        }
                        e.preventDefault();
                      }
                    }}
                  />
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {businessPlanData.financialProjections.assumptions.map((assumption, index) => (
                      <Chip
                        key={index}
                        label={assumption}
                        variant="outlined"
                        onDelete={() => {
                          setBusinessPlanData({
                            ...businessPlanData,
                            financialProjections: {
                              ...businessPlanData.financialProjections,
                              assumptions: businessPlanData.financialProjections.assumptions.filter((_, i) => i !== index)
                            }
                          });
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            )}
            
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, borderTop: 1, borderColor: 'divider', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => {
                const confirmed = window.confirm('Are you sure you want to cancel? Your progress will be saved as a draft.');
                if (confirmed) {
                  setCreateDialogOpen(false);
                  alert('Draft saved! Click "Continue Draft" to resume later.');
                }
              }}
              color="inherit"
            >
              Save & Exit
            </Button>
            <Button
              onClick={() => {
                const confirmed = window.confirm('Are you sure? This will discard all unsaved changes.');
                if (confirmed) {
                  setCreateDialogOpen(false);
                  setWizardStep(0);
                  resetForm();
                }
              }}
              color="error"
              variant="outlined"
            >
              Discard Draft
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => setWizardStep(Math.max(0, wizardStep - 1))}
              disabled={wizardStep === 0}
              variant="outlined"
            >
              Previous
            </Button>
            {wizardStep < wizardSteps.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => setWizardStep(wizardStep + 1)}
                disabled={wizardStep === 0 && (!formData.name.trim() || !formData.legal_name.trim())}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleCreate}
                disabled={!formData.name.trim() || createMutation.isPending}
                startIcon={createMutation.isPending ? <CircularProgress size={16} /> : selectedPlan ? <Edit /> : <Add />}
              >
                {createMutation.isPending 
                  ? (selectedPlan ? 'Updating...' : 'Creating...') 
                  : (selectedPlan ? 'Update Business Plan' : 'Create Business Plan')
                }
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Export Business Plan</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Choose your preferred export format for "{selectedPlan?.name}"
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<Print />}
                  onClick={() => {
                    // Open print-friendly view in new window
                    const printUrl = `/business-plans/${selectedPlan?.id}/print`;
                    window.open(printUrl, '_blank');
                    setExportDialogOpen(false);
                  }}
                  sx={{
                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                    mb: 1
                  }}
                >
                  Print Business Plan
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FileDownload />}
                  onClick={() => handleExport('pdf')}
                  disabled={exportMutation.isPending}
                >
                  Export as PDF
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FileDownload />}
                  onClick={() => handleExport('docx')}
                  disabled={exportMutation.isPending}
                >
                  Export as Word Document
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FileDownload />}
                  onClick={() => handleExport('json')}
                  disabled={exportMutation.isPending}
                >
                  Export as JSON Data
                </Button>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Compliance Results Dialog */}
      <Dialog
        open={complianceDialogOpen}
        onClose={() => setComplianceDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <CheckCircle color="primary" />
            Compliance Check Results
          </Box>
        </DialogTitle>
        <DialogContent>
          {complianceData && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, bgcolor: 'background.default', borderRadius: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      🇺🇸 SBA Compliance
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Chip
                        label={complianceData.sba_compliance?.compliant ? 'COMPLIANT' : 'INCOMPLETE'}
                        color={complianceData.sba_compliance?.compliant ? 'success' : 'warning'}
                        variant="filled"
                      />
                      <Typography
                        variant="h4"
                        color={complianceData.sba_compliance?.compliant ? 'success.main' : 'warning.main'}
                      >
                        {complianceData.sba_compliance?.percentage?.toFixed(0) || 0}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={complianceData.sba_compliance?.percentage || 0}
                      sx={{ mb: 2, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="body2" gutterBottom>
                      {complianceData.sba_compliance?.completed_sections || 0} of{' '}
                      {complianceData.sba_compliance?.total_sections || 0} sections completed
                    </Typography>
                    {complianceData.sba_compliance?.missing_sections?.length > 0 && (
                      <Box mt={2}>
                        <Typography variant="caption" color="text.secondary">
                          Missing sections:
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          {complianceData.sba_compliance.missing_sections.map((section: string, index: number) => (
                            <Chip
                              key={index}
                              label={section}
                              size="small"
                              sx={{ mt: 0.5, mr: 0.5 }}
                              color="warning"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, bgcolor: 'background.default', borderRadius: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      🇬🇧 US Compliance
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Chip
                        label={complianceData.us_compliance?.compliant ? 'COMPLIANT' : 'INCOMPLETE'}
                        color={complianceData.us_compliance?.compliant ? 'success' : 'warning'}
                        variant="filled"
                      />
                      <Typography
                        variant="h4"
                        color={complianceData.us_compliance?.compliant ? 'success.main' : 'warning.main'}
                      >
                        {complianceData.us_compliance?.percentage?.toFixed(0) || 0}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={complianceData.us_compliance?.percentage || 0}
                      sx={{ mb: 2, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="body2" gutterBottom>
                      {complianceData.us_compliance?.completed_sections || 0} of{' '}
                      {complianceData.us_compliance?.total_sections || 0} sections completed
                    </Typography>
                    {complianceData.us_compliance?.missing_sections?.length > 0 && (
                      <Box mt={2}>
                        <Typography variant="caption" color="text.secondary">
                          Missing sections:
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          {complianceData.us_compliance.missing_sections.map((section: string, index: number) => (
                            <Chip
                              key={index}
                              label={section}
                              size="small"
                              sx={{ mt: 0.5, mr: 0.5 }}
                              color="warning"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComplianceDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Financial Simulator Dialog */}
      <Dialog 
        open={simulatorDialogOpen} 
        onClose={() => setSimulatorDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Financial Simulator</DialogTitle>
        <DialogContent>
          <BusinessPlanSimulator />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSimulatorDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* HITL: Funding Application Confirmation Dialog */}
      <FundingApplicationConfirmDialog
        open={fundingConfirmDialogOpen}
        onClose={() => {
          setFundingConfirmDialogOpen(false);
          setPendingFundingApplication(null);
        }}
        planName={selectedPlan?.name || ''}
        fundingSource={pendingFundingApplication?.fundingSource || ''}
        onConfirm={handleFundingConfirm}
        loading={fundingMutation.isPending}
      />
    </Container>
  );
};

export default BusinessPlansPage;
